import {NgModule} from '@angular/core';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {IconFieldModule} from 'primeng/iconfield';
import {InputIconModule} from 'primeng/inputicon';
import {CoreModule} from '../../core/core.module';
import {CH_ENTITY_PROVIDER} from './ch.entity-provider';
import {DynamicModule} from '../../core/shared/interface/dynamic-module.interface';
import {ModuleRegistryService} from '../../core/shared/interface/module-registration.service';
import {ChCustomerTimelineNavigationComponent} from './component/ch-customer-timeline-navigation/ch-customer-timeline-navigation.component';
import {ChTimelineFeedComponent} from './component/ch-timeline-feed/ch-timeline-feed.component';
import {ChConversationHubComponent} from './component/ch-conversation-hub/ch-conversation-hub.component';
import {ChConversationOverviewComponent} from './component/ch-conversation-overview/ch-conversation-overview.component';
import {ChConversationThreadComponent} from './component/ch-conversation-thread/ch-conversation-thread.component';
import {ChConversationComposerComponent} from './component/ch-conversation-composer/ch-conversation-composer.component';
import {ChConfigPage} from './page/overview/ch-config/ch-config.page';
import {ChReviewWorkbenchPage} from './page/ch-review-workbench/ch-review-workbench.page';
import {ChReviewQueuePanelComponent} from './page/ch-review-workbench/queue/ch-review-queue-panel.component';
import {ChReviewDetailPanelComponent} from './page/ch-review-workbench/center/ch-review-detail-panel.component';
import {ChReviewResolutionPanelComponent} from './page/ch-review-workbench/resolution/ch-review-resolution-panel.component';
import {ChInboundSourceConfigObjectComponent} from './component/object-components/ch-inbound-source-config-object-component/ch-inbound-source-config-object.component';
import {ChInboxPage} from './page/ch-inbox/ch-inbox.page';
import {ChInboxListComponent} from './page/ch-inbox/list/ch-inbox-list.component';
import {ChInboxDetailPanelComponent} from './page/ch-inbox/detail/ch-inbox-detail-panel.component';
import {ChCustomerContextCardComponent} from './component/ch-customer-context-card/ch-customer-context-card.component';
import {ChReportingPage} from './page/ch-reporting/ch-reporting.page';

/**
 * Angular module for the Communication Hub (ch) feature.
 *
 * Phase 1 scope: read-only customer interaction timeline (AP5) — the container
 * {@code ChCustomerTimelineNavigationComponent} and the presentational
 * {@code ChTimelineFeedComponent}.
 *
 * Phase 2 (TM-1000001921) adds the conversation view embedded under the 'Hub'
 * tab's 'Konversationen' sub-view:
 *   - {@code ChConversationHubComponent}      — stateful container (overview ↔ thread)
 *   - {@code ChConversationOverviewComponent} — conversation list
 *   - {@code ChConversationThreadComponent}   — message thread + context panel
 *   - {@code ChConversationComposerComponent} — ticket-reply composer
 * plus the {@code ChConfigPage} (ch.ChChannelCompatibility, /ch/config).
 *
 * The container components are exported so the cr customer object page can embed
 * the hub under its 'Hub' navigation item.
 *
 * Phase 3 (TM-1000001922) adds the inbound review workbench (AP-3.8) on its own
 * route (/ch/workbench):
 *   - {@code ChReviewWorkbenchPage}            — 3-pane orchestrator page
 *   - {@code ChReviewQueuePanelComponent}      — left inbound queue (review/error)
 *   - {@code ChReviewDetailPanelComponent}     — center entry detail
 *   - {@code ChReviewResolutionPanelComponent} — right resolution panel
 * plus the inbound config object component (AP-3.9):
 *   - {@code ChInboundSourceConfigObjectComponent} — Object Page for the COMPLEX
 *     config entity ch.ChInboundSourceConfig, with left-nav over its three
 *     same-module children (WhatsApp/Mailbox/SMS configs).
 *
 * Phase 5 (TM-1000001924) adds the cross-customer Unified Inbox (AP-5.10) on its
 * own route (/ch/inbox):
 *   - {@code ChInboxPage}                — 3-pane orchestrator page (list / thread
 *     / detail+actions). The center reuses the presentational
 *     {@code ChConversationThreadComponent}; the assignment actions call the new
 *     {@code ChInboxService}.
 *   - {@code ChInboxListComponent}       — left cross-customer list (Meine/Pool/
 *     Alle tabs, window/SLA/unanswered chips, paging).
 *   - {@code ChInboxDetailPanelComponent} — right owner/assignee + assignment
 *     actions panel.
 *
 * Phase 7 (TM-1000001925) adds the reporting dashboard ("Kommunikations-Cockpit")
 * on its own route (/ch/reporting):
 *   - {@code ChReportingPage} — KPI/Overview dashboard reading the aggregated
 *     reporting stats (GET /mvsa/ch/reporting/stats?fromDays) via the new
 *     {@code ChReportingService}: headline KPI cards, "Volumen je Kanal" chart
 *     (p-chart with a p-table fallback), SLA breakdown and Auslastung tables
 *     (load per pool / per agent), plus a 7/30/90-day timeframe selector.
 *
 * @ticket TM-1000001919
 * @ticket TM-1000001921
 * @ticket TM-1000001922
 * @ticket TM-1000001924
 * @ticket TM-1000001925
 */
@NgModule({
    declarations: [
        ChCustomerTimelineNavigationComponent,
        ChTimelineFeedComponent,
        ChConversationHubComponent,
        ChConversationOverviewComponent,
        ChConversationThreadComponent,
        ChConversationComposerComponent,
        ChConfigPage,
        ChReviewWorkbenchPage,
        ChReviewQueuePanelComponent,
        ChReviewDetailPanelComponent,
        ChReviewResolutionPanelComponent,
        ChInboundSourceConfigObjectComponent,
        ChInboxPage,
        ChInboxListComponent,
        ChInboxDetailPanelComponent,
        ChCustomerContextCardComponent,
        ChReportingPage,
    ],
    exports: [
        ChCustomerTimelineNavigationComponent,
        ChTimelineFeedComponent,
        ChConversationHubComponent,
    ],
    imports: [
        CoreModule,
        ConfirmDialogModule,
        // PrimeNG v20 icon-field for the inbox search input (UX review TOP-4) —
        // imported directly because SharedModule does not re-export them.
        IconFieldModule,
        InputIconModule,
    ],
    providers: [CH_ENTITY_PROVIDER]
})
export class ChModule implements DynamicModule {

    alias = 'ch';

    constructor(private moduleRegistry: ModuleRegistryService) {
        this.moduleRegistry.registerModule(this);
    }

    // TM-1000001919: ch has no dedicated KPI/statistics components in Phase 1
    // (read-only timeline only) — return null, matching the ca/cw module pattern.
    getKpiComponent() {
        return null;
    }

    getStatisticsComponent() {
        return null;
    }

}
